import { getParsedWindowObject, WindowObject } from 'fe-core-injected';
import { ReactNode, createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

type AppState = Pick<WindowObject, 'appState'>;
type WindowConfig = AppState['appState']['config'];
type WindowExt = AppState['appState']['ext'];
type UserInfo = WindowObject['userInfo'];

export type TConfig = {
  config: WindowConfig;
  extension: WindowExt;
  userInfo: UserInfo;
};

const WindowObjectContext = createContext<TConfig | null>(null);

const queryFn = async () => {
  const windowObject = await getParsedWindowObject();
  return {
    config: windowObject.appState.config,
    extension: windowObject.appState?.ext,
    userInfo: windowObject.userInfo,
  };
};
export const useConfigContext = () => {
  const context = useContext(WindowObjectContext);
  if (!context) {
    throw new Error('WindowObjectContext: No value provided');
  }
  return context;
};

export const ConfigContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['config'],
    queryFn,
  });

  if (isLoading) return null;

  if (isError) {
    throw error;
  }

  if (!data) {
    throw new Error('WindowObjectContext: No data found on window object');
  }

  return (
    <WindowObjectContext.Provider value={data}>
      {children}
    </WindowObjectContext.Provider>
  );
};
